import { AdminLandlord, CropperCanvasData } from '@root/types';
import api from '@shared/api';
import routes from '@root/routes';
import { useTranslation } from 'react-i18next';
import { Notification, PopperTooltip } from '@components/shared';
import { InfoIcon, Switch, ToggleEvent, Dialog } from '@viewthespace/components';
import { UploadApiResponse as CloudinaryResponse } from 'cloudinary';
import { useState } from 'react';
import useCloudinaryUploader from '@components/shared/Cloudinary/useCloudinaryUploader';
import useCropperModal, {
  warningDimensionPresets,
} from '@components/shared/Cropper/useCropperModal';
import EditableAsset from '@components/shared/EditableAsset/EditableAsset';
import AssetUploader from '@components/shared/EditableAsset/AssetUploader';
import css from './BrandedAssets.module.less';

type Props = {
  landlord: AdminLandlord;
  fetchLandlord: () => void;
};

type LogoTypes = 'bannerImage' | 'wordmarkLogo' | 'lettermarkLogo';

const BrandedAssets = ({ landlord, fetchLandlord }: Props) => {
  const { t } = useTranslation('admin');

  const { CropperModal, cropperModalProps, openCropperModal } = useCropperModal();
  const [uploadNewPhotoType, setUploadNewPhotoType] = useState<LogoTypes | null>(null);

  const [deleteConfig, setDeleteConfig] = useState<{
    isOpen: boolean;
    assetType: LogoTypes | null;
  }>({ isOpen: false, assetType: null });

  const handleToggleChange = async (event: ToggleEvent<HTMLInputElement>) => {
    await updateLandlord({ displayBannerOnBuildings: event.value, skipMarketChanges: true });
  };

  const updateLandlord = async payload => {
    const apiResponse = await api.put(routes.api.admin.landlord(landlord?.id), payload);
    if (apiResponse.ok) {
      fetchLandlord();
      Notification.info({
        title: t('common:changesSaved'),
        text: t('landlord.updateSuccess', { name: landlord!.companyName }),
        placement: 'topRight',
      });
    }
  };

  type EditImageArgs = { cloudinaryId: string; cropperCanvasData?: CropperCanvasData | null };
  const editLettermarkLogo = ({ cloudinaryId, cropperCanvasData }: EditImageArgs) => {
    openCropperModal({
      cloudinaryId,
      initialCanvasData: cropperCanvasData,
      title: t('landlord.brandedAssets.editModal.lettermarkLogo.title'),
      cropBoxDimentions: { height: 295, width: 295 },
      warningDimensions: warningDimensionPresets.lettermark,
      onSave: onSave('lettermarkLogo'),
    });
  };

  const editWordmarkLogo = ({ cloudinaryId, cropperCanvasData }: EditImageArgs) => {
    openCropperModal({
      cloudinaryId,
      initialCanvasData: cropperCanvasData,
      title: t('landlord.brandedAssets.editModal.wordmarkLogo.title'),
      notEditableMessage: t('landlord.brandedAssets.editModal.wordmarkLogo.nonEditable'),
      cropBoxDimentions: { height: 80, width: 482 },
      warningDimensions: warningDimensionPresets.wordmark,
      viewMode: 'preview',
      trimImage: true,
      onSave: onSave('wordmarkLogo'),
    });
  };

  const editBannerImage = ({ cloudinaryId, cropperCanvasData }: EditImageArgs) => {
    openCropperModal({
      cloudinaryId,
      initialCanvasData: cropperCanvasData,
      title: t('landlord.brandedAssets.editModal.banner.title'),
      cropBoxDimentions: { height: 93, width: 649 },
      warningDimensions: warningDimensionPresets.banner,
      onSave: onSave('bannerImage'),
    });
  };

  const onReady = (assetType: LogoTypes | null) => async (response: CloudinaryResponse) => {
    const cloudinaryId = response.public_id;
    if (assetType === 'lettermarkLogo') {
      editLettermarkLogo({ cloudinaryId });
    } else if (assetType === 'wordmarkLogo') {
      editWordmarkLogo({ cloudinaryId });
    } else if (assetType === 'bannerImage') {
      editBannerImage({ cloudinaryId });
    }
  };

  const onSave =
    (assetType: LogoTypes) =>
    ({ cloudinaryId, cropperCanvasData, cloudinaryTransformations }) => {
      updateLandlord({
        [assetType]: {
          cloudinaryId,
          cropperCanvasData,
          transformations: cloudinaryTransformations,
        },
        skipMarketChanges: true,
      });
    };

  const createErrorNotification = (error: string = '') => {
    Notification.error({
      title: t('notification.updateError'),
      text: error,
    });
  };

  const {
    selectAndUploadToCloudinary,
    CloudinaryUploaderFileInput,
    cloudinaryUploaderFileInputProps,
  } = useCloudinaryUploader({
    uploadToVTS: false,
    onReady: onReady(uploadNewPhotoType),
    onError: createErrorNotification,
  });

  const onUpload = (assetType: LogoTypes) => {
    setUploadNewPhotoType(assetType);
    if (selectAndUploadToCloudinary) selectAndUploadToCloudinary();
  };

  const onClose = () => {
    setDeleteConfig({ isOpen: false, assetType: null });
  };

  const onDeleteConfirm = async () => {
    if (deleteConfig.assetType) {
      await updateLandlord({
        [deleteConfig.assetType]: {
          cloudinaryId: null,
        },
        ...(deleteConfig.assetType === 'bannerImage' && { displayBannerOnBuildings: false }),
        skipMarketChanges: true,
      });
      setDeleteConfig({ isOpen: false, assetType: null });
    }
  };

  return (
    <>
      {deleteConfig.isOpen && (
        <DeleteDialog
          isDeleteOpen={deleteConfig.isOpen}
          assetType={deleteConfig.assetType!}
          onClose={onClose}
          onConfirm={onDeleteConfirm}
        />
      )}
      <CloudinaryUploaderFileInput {...cloudinaryUploaderFileInputProps} />
      <div className="mt-3.5">
        <span className="font-title">{t('landlord.brandedAssets.logos')}</span>
      </div>
      <div className="mt-4 flex flex-col">
        <span className="font-subtitle">{t('landlord.brandedAssets.lettermarkLogo.title')}</span>
        <div className="flex flex-row">
          <span className="font-body-medium">
            {t('landlord.brandedAssets.lettermarkLogo.subtitle')}
          </span>
          <PopperTooltip
            showArrow
            arrowElementClassName={css.arrow}
            trigger="click"
            toolTipPlacement="top"
            triggerElement={<InfoIcon className="mb-1 ml-1 text-indigo-100" size="sm" />}
            popperElement={
              <figure>
                <img
                  src="https://media.truva.com/assets/lettermark-logo-screenshot.png"
                  alt="screenshot of lettermark logo on the landlord page"
                />
              </figure>
            }
          />
        </div>

        <div className="pt-2">
          {landlord.lettermarkLogo?.cloudinaryId ? (
            <div className="flex flex-col">
              <EditableAsset
                imagePath={landlord.lettermarkLogo?.path}
                menuPlacement="right-start"
                imageClassName="!h-[110px] w-[110px] rounded-[3px] !border !border-solid !border-black-020 object-cover"
                altText="Lettermark logo that appears on the Lanldord page"
                editButtonClassName="relative ml-1 top-[46px] border border-solid border-black-035"
                editButtonName="lettermark-logo-edit-button"
                onUpload={() => onUpload('lettermarkLogo')}
                onDelete={() => setDeleteConfig({ isOpen: true, assetType: 'lettermarkLogo' })}
                onEdit={() => editLettermarkLogo(landlord.lettermarkLogo!)}
              />
              <span className="pt-1 text-black-055 font-body-small">
                {t('landlord.brandedAssets.lettermarkLogo.imageSize')}
              </span>
            </div>
          ) : (
            <AssetUploader
              placeholderContainerClassName="flex flex-col items-center pl-[10px] pr-1"
              placeholderElementClassName="text-center font-body-medium"
              containerClassName="w-[110px] !h-[110px] !my-0"
              recommendedImageText={t('landlord.brandedAssets.lettermarkLogo.imageSize')}
              onReady={onReady('lettermarkLogo')}
              onError={createErrorNotification}
              testId="lettermark-logo-uploader"
            />
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-col">
        <span className="font-subtitle">{t('landlord.brandedAssets.wordmarkLogo.title')}</span>
        <div className="flex flex-row">
          <span className="font-body-medium">
            {t('landlord.brandedAssets.wordmarkLogo.subtitle')}
          </span>
          <PopperTooltip
            showArrow
            trigger="click"
            toolTipPlacement="top"
            triggerElement={<InfoIcon className="mb-1 ml-1 text-indigo-100" size="sm" />}
            popperElement={
              <figure>
                <img
                  src="https://media.truva.com/assets/wordmark-logo-screenshot.png"
                  alt="screenshot of wordmark logo on the search page"
                />
              </figure>
            }
          />
        </div>

        <div className="pt-2">
          {landlord.wordmarkLogo?.cloudinaryId ? (
            <div className="flex flex-col">
              <EditableAsset
                imagePath={landlord.wordmarkLogo?.largePath}
                menuPlacement="right-start"
                imageClassName="mt-[-6px] !max-h-[20px] !max-w-[120px] rounded-[3px] !border !border-solid !border-black-020 object-contain"
                altText="Wordmark Logo that appears on the search page"
                editButtonClassName="ml-1 border border-solid border-black-035"
                editButtonName="wordmark-logo-edit-button"
                onUpload={() => onUpload('wordmarkLogo')}
                onDelete={() => setDeleteConfig({ isOpen: true, assetType: 'wordmarkLogo' })}
                onPreview={() => editWordmarkLogo(landlord.wordmarkLogo!)}
                items={['upload', 'preview', 'delete']}
              />
              <span className="pt-0.5 text-black-055 font-body-small">
                {t('landlord.brandedAssets.wordmarkLogo.imageSize')}
              </span>
            </div>
          ) : (
            <AssetUploader
              placeholderElementClassName="pl-[4px] font-body-medium"
              containerClassName="!w-[257px] !h-[52px] !my-0"
              onReady={onReady('wordmarkLogo')}
              onError={createErrorNotification}
              recommendedImageText={t('landlord.brandedAssets.wordmarkLogo.imageSize')}
              testId="wordmark-logo-uploader"
            />
          )}
        </div>
      </div>
      <div className="mt-6 h-[1px] w-full bg-black-010" />
      <div className="mt-6 flex flex-col">
        <span className="font-title">{t('landlord.brandedAssets.banner.title')}</span>
        <div className="mt-4 flex flex-row">
          <span className="font-body-medium">{t('landlord.brandedAssets.banner.subtitle')}</span>
          <PopperTooltip
            showArrow
            toolTipPlacement="top"
            trigger="click"
            triggerElement={<InfoIcon className="mb-1 ml-1 text-indigo-100" size="sm" />}
            popperElement={
              <figure>
                <img
                  src="https://media.truva.com/assets/banner-landlord-page-screenshot.png"
                  alt="screenshot of a banner on the Landlord page"
                />
              </figure>
            }
          />
        </div>

        <div className="pb-3 pt-2">
          {landlord.bannerImage?.cloudinaryId ? (
            <>
              <EditableAsset
                containerClassName="h-[139px] relative max-w-[1440px] w-full"
                imagePath={landlord.bannerImage.path}
                menuPlacement="top-end"
                imageClassName="!h-[139px] w-[1440px] rounded-[3px] !border !border-solid !border-black-020 object-cover"
                altText="Panoramic Banner that appears on the Landlord page"
                editButtonClassName="absolute bottom-1 right-1 border-black-035 border border-solid"
                editButtonName="banner-edit-button"
                onUpload={() => onUpload('bannerImage')}
                onDelete={() => setDeleteConfig({ isOpen: true, assetType: 'bannerImage' })}
                onEdit={() => editBannerImage(landlord.bannerImage!)}
              />
              <span className="flex pt-1 text-black-055 font-body-small">
                {t('landlord.brandedAssets.banner.imageSize')}
              </span>
            </>
          ) : (
            <AssetUploader
              placeholderElementClassName="pl-1 font-body-medium"
              onReady={onReady('bannerImage')}
              onError={createErrorNotification}
              containerClassName="!h-[139px] !my-0 w-full max-w-[1440px]"
              recommendedImageText={t('landlord.brandedAssets.banner.imageSize')}
              testId="banner-uploader"
            />
          )}
        </div>
        <div className="flex flex-row pb-3">
          <Switch
            name="DisplayBannerOnBuildingToggle"
            labelPosition="right"
            label={t('landlord.brandedAssets.banner.displayOnBuildingsLabel')}
            className="font-body-medium-emphasis"
            onChange={handleToggleChange}
            isOn={landlord.displayBannerOnBuildings}
            isDisabled={!landlord.bannerImage?.cloudinaryId}
            testId="banner-toggle"
          />
        </div>
      </div>
      <CropperModal {...cropperModalProps} />
    </>
  );
};

const DeleteDialog = ({
  isDeleteOpen,
  assetType,
  onClose,
  onConfirm,
}: {
  isDeleteOpen: boolean;
  assetType: LogoTypes;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('admin');

  const assetConfig = {
    lettermarkLogo: {
      title: t('landlord.brandedAssets.deleteModal.lettermarkLogo.title'),
      text: t('landlord.brandedAssets.deleteModal.lettermarkLogo.text'),
    },
    wordmarkLogo: {
      title: t('landlord.brandedAssets.deleteModal.wordmarkLogo.title'),
      text: t('landlord.brandedAssets.deleteModal.wordmarkLogo.text'),
    },
    bannerImage: {
      title: t('landlord.brandedAssets.deleteModal.banner.title'),
      text: t('landlord.brandedAssets.deleteModal.banner.text'),
    },
  };

  return (
    <Dialog
      align="default"
      header={assetConfig[assetType].title}
      isOpen={isDeleteOpen}
      onClose={onClose}
      closeOnClickAway
      width="small"
      primaryButton={{
        text: t('common:confirm'),
        onClick: onConfirm,
      }}
      secondaryButton={{
        text: t('common:cancel'),
        onClick: onClose,
      }}
    >
      <div className="p-2">
        <span className="font-body-medium">{assetConfig[assetType].text}</span>
      </div>
    </Dialog>
  );
};

export default BrandedAssets;
